import {
  Amex,
  Calculator,
  Close,
  EligibleProducts,
  Facebook,
  FsaTracker,
  Heart,
  HelpContact,
  HsaPerksDashboard,
  Instagram,
  Lightbulb,
  LinkedIn,
  LiveChat,
  Mastercard,
  Ring,
  TrackManageOrder,
  Twitter,
  User,
  Visa,
  WeightLoss,
} from "~/hec/assets/icons";

import type { IconComponentMap } from "./base";

/**
 * COM-188 — the audited chrome subset of the IconPicker icon map.
 *
 * The full map (./icon-map.ts) references all ~113 icon modules, and the
 * chrome registry ships in the always-loaded layout chunk — the map alone put
 * ~167KB of icon code on every route's critical path. This module binds only
 * the icons the chrome entries actually use, via named imports so
 * tree-shaking keeps the rest out of the layout chunk.
 *
 * Same three-way pinning as the chrome block registry itself:
 *
 *  1. AUDIT (2026-08-27): every published entry of the 5 chrome models was
 *     fetched from all Builder spaces (MAIN + QA + SANDBOX + DEVELOPMENT,
 *     FSA/HSA/WDH) and every IconPicker `iconName` collected. This map is
 *     exactly that union. Regenerate with `node scripts/audit-chrome-blocks.mjs`
 *     (it also prints the icon union and pins iconName in the fixtures).
 *  2. FIXTURE TEST: tests/unit/hec/builder/chrome-registry.test.ts fails if a
 *     MAIN-space chrome entry uses an icon missing here.
 *  3. RUNTIME GUARD: an unmapped name warn-logs once and renders the same
 *     fallback `<span data-icon-name>` on server AND client (no hydration
 *     mismatch, no blank block) — a missing glyph, not a missing surface.
 *
 * Adding an icon to a chrome entry in Builder? Add its named import + map
 * entry here and re-run the audit script.
 */
export const chromeIconMap: IconComponentMap = {
  Amex,
  Calculator,
  Close,
  EligibleProducts,
  Facebook,
  FsaTracker,
  Heart,
  HelpContact,
  HsaPerksDashboard,
  Instagram,
  Lightbulb,
  LinkedIn,
  LiveChat,
  Mastercard,
  Ring,
  TrackManageOrder,
  Twitter,
  User,
  Visa,
  WeightLoss,
};

/** One warning per icon name per isolate/session — greppable, not spammy. */
const warnedIconNames = new Set<string>();

export function warnUnknownChromeIcon(iconName: string): void {
  if (warnedIconNames.has(iconName)) return;
  warnedIconNames.add(iconName);
  console.warn(
    `[chrome-registry] a chrome Builder entry uses IconPicker icon "${iconName}" ` +
      `that is not in the chrome icon map — it renders as an empty span. Add ` +
      `it to chromeIconMap (app/hec/builder/blocks/components/icon-picker/` +
      `chrome-icon-map.ts) and re-run scripts/audit-chrome-blocks.mjs.`,
  );
}
